import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  signUp,
  signIn,
  confirmSignIn,
  autoSignIn,
  confirmSignUp,
  resendSignUpCode,
  fetchAuthSession,
  signOut,
  signInWithRedirect,
  resetPassword,
  confirmResetPassword,
  updatePassword,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { environment } from '../../../../../environments/environment';

export interface RegisterResult {
  success: boolean;
  needsConfirmation?: boolean;
  error?: string;
  errorName?: string;
}

interface ProviderCheckResult {
  success: boolean;
  error?: string;
  errorName?: string;
}

// Held in memory only for the duration of the verification step
interface PendingRegistration {
  cognitoSub: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  marketingConsent: boolean;
}

export interface UserProfile {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  city: string | null;
  phone: string | null;
  doc_type: 'CC' | 'TI' | 'CE' | 'PA' | 'NIT' | null;
  doc_number: string | null;
  birth_date: string | null;
  registered_at: string;
  has_active_subscription: boolean;
  marketing_consent: boolean;
}

export interface UpdateProfilePayload {
  first_name: string;
  last_name: string;
  phone: string | null;
  city: string | null;
  birth_date: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  // JWT stored in memory — never in localStorage or sessionStorage
  private accessToken = signal<string | null>(null);
  private readonly PROVIDER_MISMATCH_KEY = 'eldiario_provider_mismatch';

  // Temporary data kept until email is confirmed
  private pendingRegistration: PendingRegistration | null = null;

  // True while loginWithEmail() is in progress — prevents the Hub listener
  // from treating an email/password sign-in as an OAuth sign-in
  private emailLoginInProgress = false;

  // Set to true after any successful login/register so the panel can show a welcome toast once
  readonly justLoggedIn = signal(false);

  // Stores the authenticated user's profile — populated on login/register, null on logout
  readonly currentUser = signal<UserProfile | null>(null);

  // Derived signal: true when the current user has an active subscription
  readonly hasActiveSubscription = signal(false);
  readonly oauthProviderError = signal<string | null>(null);

  /** Central setter — always updates both currentUser and hasActiveSubscription together. */
  private setCurrentUser(user: UserProfile | null): void {
    this.currentUser.set(user);
    this.hasActiveSubscription.set(user?.has_active_subscription === true);
    // Refrescar la cookie de paywall siempre que se establece un usuario autenticado:
    // login email, registro, OAuth, auto-restore, challenge de nueva contraseña.
    // Fire-and-forget: no bloquea el flujo. No se llama cuando user === null (logout).
    if (user) this.refreshPaywallCookie();
  }

  constructor() {
    // Listen for OAuth sign-in events (e.g., Google via signInWithRedirect).
    // When Amplify completes the code exchange after a redirect, it fires
    // 'signedIn'. We grab the token and upsert the profile in the backend.
    Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signedIn') {
        // Only handle OAuth (Google/Facebook) sign-ins here.
        // Email and registration flows manage their own session token internally.
        if (!this.pendingRegistration && !this.emailLoginInProgress) {
          await this.loadSessionToken();
          const completed = await this.finalizeOAuthPopupSession();
          if (!completed && this.oauthProviderError() && !this.isEmbeddedContext() && !this.isPopupContext()) {
            await this.router.navigate(['/login'], {
              state: { authProviderError: this.oauthProviderError() },
            });
          }
        }
      }
    });
  }

  // ============ STEP 1 — REGISTER (sends verification code) ============
  async registerUser(
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    password: string,
    marketingConsent: boolean,
  ): Promise<RegisterResult> {
    try {
      const providerCheck = await this.checkProviderAllowed(email, 'email');
      if (!providerCheck.success) return providerCheck;

      const { nextStep, userId } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            phone_number: `+57${phone}`,
            given_name: firstName,
            family_name: lastName,
          },
          autoSignIn: true,
        },
      });

      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        // Store data temporarily — needed after the user confirms
        const cognitoSub = userId ?? '';
        this.pendingRegistration = { cognitoSub, email, firstName, lastName, phone, marketingConsent };
        // Persist the canonical identity before email confirmation. This remains
        // pending if the browser closes; a later verified login promotes it.
        this.preRegisterInBackend({ cognitoSub, email, firstName, lastName, phone, marketingConsent });
        return { success: true, needsConfirmation: true };
      }

      // Pool without email verification (COMPLETE_AUTO_SIGN_IN / DONE)
      const token = await this.resolveSessionAfterSignUp(nextStep.signUpStep, email, password);
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);
      const regProfile = await this.saveProfileInBackend({
        cognitoSub: userId ?? '', email, firstName, lastName, phone, marketingConsent,
      });
      if (regProfile) this.setCurrentUser(regProfile);
      this.justLoggedIn.set(true);

      return { success: true };
    } catch (error: any) {
      console.error('Error in registerUser:', error);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    }
  }

  // ============ STEP 2 — CONFIRM CODE ============
  async confirmRegistration(email: string, code: string): Promise<RegisterResult> {
    try {
      await confirmSignUp({ username: email, confirmationCode: code });

      // Amplify v6 requires autoSignIn() to complete the session after confirmSignUp().
      // Edge case: if a stale session exists (user didn't sign out before re-registering),
      // autoSignIn() throws UserAlreadyAuthenticatedException — we catch it and proceed
      // straight to fetchAuthSession(), which will return the new user's token because
      // confirmSignUp() already replaced the internal Amplify auth state.
      try {
        await autoSignIn();
      } catch {
        // UserAlreadyAuthenticatedException or similar — session is already active
      }

      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);

      // The normal path finds the pending profile and promotes it to active.
      // If the non-blocking pre-register request failed, create the profile now.
      let confirmProfile: UserProfile | null = null;
      try {
        const anonymousId = this.getAnonymousId();
        const loginRes = await firstValueFrom(
          this.http.post<{ success: boolean; data: UserProfile }>(
            `${environment.apiUrl}/client/auth/login`,
            {},
            anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
          ),
        );
        confirmProfile = loginRes.data ?? null;
      } catch (loginError: any) {
        if (loginError?.status !== 404 || !this.pendingRegistration) throw loginError;
        confirmProfile = await this.saveProfileInBackend(this.pendingRegistration);
      }

      this.pendingRegistration = null;
      if (confirmProfile) this.setCurrentUser(confirmProfile);
      this.justLoggedIn.set(true);

      return { success: true };
    } catch (error: any) {
      console.error('Error in confirmRegistration:', error);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    }
  }

  // ============ CONFIRM SIGN-UP ONLY (used from login flow) ============
  // Confirms the account without attempting autoSignIn or syncing a backend
  // profile. Used when the user arrives at the verification screen from the
  // login page (no pendingRegistration, no autoSignIn session available).
  async confirmSignUpOnly(email: string, code: string): Promise<RegisterResult> {
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      return { success: true };
    } catch (error: any) {
      console.error('Error in confirmSignUpOnly:', error);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    }
  }

  // ============ RESEND VERIFICATION CODE ============
  async resendConfirmationCode(email: string): Promise<RegisterResult> {
    try {
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (error: any) {
      console.error('Error in resendConfirmationCode:', error);
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    }
  }

  // ============ TOKEN ACCESS ============
  getAccessToken(): string | null {
    return this.accessToken();
  }

  async postEmbeddedSessionToParent(authEvent?: 'login' | 'register' | 'sync'): Promise<void> {
    const isPopup = this.isPopupContext();
    if (!this.isEmbeddedContext() && !isPopup) return;

    try {
      const { tokens } = await fetchAuthSession();
      const accessToken = tokens?.accessToken?.toString() ?? null;
      const idToken = tokens?.idToken?.toString() ?? null;
      if (!accessToken && !idToken) return;

      const user = this.currentUser();
      const displayName = user
        ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email
        : null;

      const payload: any = {
        access_token: accessToken,
        id_token: idToken,
        auth_event: authEvent ?? 'login',
      };

      if (user) {
        payload.user = {
          display_name: displayName,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          has_active_subscription: user.has_active_subscription === true,
        };
      } else if (displayName) {
        payload.user = { display_name: displayName };
      }

      const targetOrigin = environment.wordpressUrl;

      if (isPopup && window.opener) {
        // Popup mode (OAuth social login): the popup was opened from the
        // embedded login iframe, so window.opener IS the iframe window, NOT
        // the WordPress page. We need to send the session to the WordPress
        // page, which is window.opener.parent (the iframe's parent).
        // Both postMessage calls are cross-origin (Angular → WordPress) which
        // is always allowed; only reading cross-origin properties is restricted.
        try {
          let target: Window = window.opener as Window;
          try {
            // If opener is inside an iframe, opener.parent is the top-level WP page.
            const openerParent = (window.opener as Window).parent;
            if (openerParent && openerParent !== (window.opener as Window)) {
              target = openerParent;
            }
          } catch { /* opener is top-level or cross-origin guard — use opener directly */ }

          target.postMessage({ type: 'eldiario:session', payload }, targetOrigin);
          target.postMessage({ type: 'eldiario:close-modal' }, targetOrigin);
        } catch { /* opener may have been closed */ }
        try { sessionStorage.removeItem('eld_oauth_popup'); } catch { /* no-op */ }
        // Give the message time to be processed before closing
        setTimeout(() => window.close(), 400);
      } else {
        // Iframe mode (normal embedded login): post to parent frame (WordPress).
        window.parent.postMessage({ type: 'eldiario:session', payload }, targetOrigin);
        window.parent.postMessage({ type: 'eldiario:close-modal' }, targetOrigin);
      }
    } catch {
      return;
    }
  }

  // Returns true when this Angular instance is running inside a popup window
  // that was opened by the OAuth social login flow (not a normal tab or iframe).
  private isPopupContext(): boolean {
    try {
      return !!window.opener &&
             window.opener !== window &&
             sessionStorage.getItem('eld_oauth_popup') === '1';
    } catch {
      return false;
    }
  }

  // ============ LOAD / VERIFY SESSION (used by AuthGuard) ============
  // Fetches the active Cognito session, stores the access token in the signal,
  // and returns true if authenticated. Works for both email and OAuth sessions.
  async loadSessionToken(): Promise<boolean> {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (token) {
        this.accessToken.set(token);
        return true;
      }
    } catch { /* fall through to sessionStorage */ }

    // Fallback: restore from the handoff token saved in sessionStorage.
    // This covers page reloads when the user arrived via WordPress ?_t= since
    // Amplify uses in-memory storage in embed mode and has nothing in localStorage.
    try {
      const saved = sessionStorage.getItem('eld_ht');
      if (saved) {
        const parts = saved.split('.');
        if (parts.length >= 3) {
          const raw     = parts[1].replace(/-/g, '+').replace(/_/g, '/');
          const padding = raw.length % 4 ? '='.repeat(4 - (raw.length % 4)) : '';
          const payload = JSON.parse(atob(raw + padding));
          if (payload?.sub && (!payload.exp || payload.exp * 1000 > Date.now())) {
            this.accessToken.set(saved);
            if (!this.currentUser()) await this.getProfile().catch(() => {});
            return true;
          }
        }
        // Token invalid or expired — discard it
        sessionStorage.removeItem('eld_ht');
      }
    } catch { /* no-op */ }

    this.accessToken.set(null);
    return false;
  }

  // ============ RESTORE FROM HANDOFF TOKEN (cross-domain from WordPress) ============
  // Called by authGuard when WordPress passes the Cognito access token via
  // the ?_t= URL parameter (workaround for Chrome CHIPS / Safari ITP storage
  // partitioning that prevents the iframe from writing to unpartitioned storage).
  async restoreFromHandoffToken(token: string): Promise<boolean> {
    try {
      const parts = token.split('.');
      if (parts.length < 3) return false;

      // Decode and validate the JWT payload (signature verified by backend on each request)
      const raw     = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padding = raw.length % 4 ? '='.repeat(4 - (raw.length % 4)) : '';
      const payload = JSON.parse(atob(raw + padding));

      if (!payload?.sub) return false;
      if (payload.exp && payload.exp * 1000 < Date.now()) return false;

      this.accessToken.set(token);

      // Persist token in sessionStorage so page reloads within the same tab
      // keep the session (covers reload after WordPress ?_t= handoff).
      try { sessionStorage.setItem('eld_ht', token); } catch { /* no-op */ }

      // Load profile if not already in memory
      if (!this.currentUser()) {
        await this.getProfile().catch(() => {});
      }

      return true;
    } catch {
      return false;
    }
  }

  // ============ GOOGLE OAUTH ============
  async loginWithGoogle(email?: string): Promise<ProviderCheckResult> {
    const providerCheck = await this.checkProviderAllowed(email ?? '', 'google');
    if (!providerCheck.success) return providerCheck;

    if (this.isEmbeddedContext()) {
      // Google blocks OAuth inside iframes (HTTP 403). Open a popup instead so
      // the authorization flow runs in a full browser window. After Cognito
      // redirects back to /panel/subscription, the popup posts the session to
      // window.opener (WordPress) and closes itself.
      window.open(
        `${window.location.origin}/oauth-popup?provider=google`,
        'eld_oauth',
        'width=520,height=680,resizable=yes,scrollbars=yes'
      );
      return { success: true };
    }
    await signInWithRedirect({ provider: 'Google' });
    return { success: true };
  }

  // ============ FACEBOOK OAUTH ============
  async loginWithFacebook(email?: string): Promise<ProviderCheckResult> {
    const providerCheck = await this.checkProviderAllowed(email ?? '', 'facebook');
    if (!providerCheck.success) return providerCheck;

    if (this.isEmbeddedContext()) {
      // Same iframe restriction applies to Facebook OAuth.
      window.open(
        `${window.location.origin}/oauth-popup?provider=facebook`,
        'eld_oauth',
        'width=520,height=680,resizable=yes,scrollbars=yes'
      );
      return { success: true };
    }
    await signInWithRedirect({ provider: 'Facebook' });
    return { success: true };
  }

  // ============ EMAIL / PASSWORD LOGIN ============
  async loginWithEmail(email: string, password: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    const providerCheck = await this.checkProviderAllowed(email, 'email');
    if (!providerCheck.success) return providerCheck;

    this.emailLoginInProgress = true;
    try {
      const { isSignedIn, nextStep } = await signIn({ username: email, password });

      // Cognito requires the user to set a new password (admin-created account or temporary password).
      // Amplify v6 surfaces both the internal PASSWORD_VERIFIER SRP step and the subsequent
      // NEW_PASSWORD_REQUIRED challenge as a single signInStep value.
      if (!isSignedIn && nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Keep the Amplify challenge session alive so the login component can
        // call confirmSignIn() to let the user set their new permanent password.
        return { success: false, errorName: 'NewPasswordRequired' };
      }

      // Amplify v6 does NOT throw UserNotConfirmedException — instead it returns
      // isSignedIn: false with signInStep 'CONFIRM_SIGN_UP' when the account
      // hasn't been verified yet.
      if (!isSignedIn && nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        signOut().catch(() => {});
        return { success: false, errorName: 'UserNotConfirmedException' };
      }

      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);

      // Notify backend: verifies JWT and confirms user exists in DB
      const anonymousId = this.getAnonymousId();
      const loginRes = await firstValueFrom(
        this.http.post<{ success: boolean; data: UserProfile }>(
          `${environment.apiUrl}/client/auth/login`,
          {},
          anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
        )
      );
      this.setCurrentUser(loginRes.data ?? null);
      this.justLoggedIn.set(true);

      return { success: true };
    } catch (error: any) {
      this.accessToken.set(null);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      if (error?.status === 404) {
        signOut().catch(() => {});
        return { success: false, errorName: 'UserProfileNotFound', error: 'No encontramos tu cuenta. Por favor regístrate primero.' };
      }
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    } finally {
      this.emailLoginInProgress = false;
    }
  }

  // ============ COMPLETE NEW_PASSWORD_REQUIRED CHALLENGE ============
  // Called from the login inline form after the user sets their new password.
  // Amplify must have an active challenge session (loginWithEmail returned
  // NewPasswordRequired without signOut), otherwise this will throw.
  async confirmNewPassword(newPassword: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) throw { name: 'NetworkError' };
      this.accessToken.set(token);

      const anonymousId = this.getAnonymousId();
      const loginRes = await firstValueFrom(
        this.http.post<{ success: boolean; data: any }>(
          `${environment.apiUrl}/client/auth/login`,
          {},
          anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
        )
      );
      this.setCurrentUser(loginRes.data ?? null);
      this.justLoggedIn.set(true);
      return { success: true };
    } catch (error: any) {
      this.accessToken.set(null);
      const providerMismatch = this.providerMismatchResult(error);
      if (providerMismatch) return providerMismatch;
      if (error?.status === 404) {
        signOut().catch(() => {});
        return { success: false, errorName: 'UserProfileNotFound', error: 'No encontramos tu cuenta. Por favor regístrate primero.' };
      }
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    }
  }
  // ============ STORE SESSION IN WORDPRESS LOCALSTORAGE ============
  // Writes the current session to localStorage under the same key that
  // events.js reads, so the WordPress UI updates without a page reload
  // when auth happens via the web-component modal (not an iframe).
  async storeSessionLocally(): Promise<void> {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens?.accessToken?.toString();
      if (!token) return;
      const user = this.currentUser();
      const displayName = user
        ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email
        : null;
      const sessionData: Record<string, unknown> = { access_token: token };
      if (user) {
        sessionData['user'] = {
          display_name: displayName,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          has_active_subscription: user.has_active_subscription === true,
        };
      }
      localStorage.setItem('eldiario_session', JSON.stringify(sessionData));
    } catch { /* best-effort */ }
  }

  // ============ REFRESH PAYWALL COOKIE ============
  // Llama al backend para que refresque la cookie HttpOnly eldiario_paywall
  // con el estado de suscripción actualizado. Fire-and-forget: no bloquea el flujo.
  // Se llama después de login exitoso y después de compra de suscripción exitosa.
  refreshPaywallCookie(): void {
    const token = this.accessToken();
    if (!token) return;
    this.http.post(
      `${environment.apiUrl}/client/auth/refresh-cookie`,
      {},
      { withCredentials: true },
    ).subscribe({ error: () => {} });
  }

  // ============ CLEAR SESSION (used by interceptor on 401) ============
  clearSession(): void {
    this.accessToken.set(null);
    this.setCurrentUser(null);
    this.pendingRegistration = null;
    try { sessionStorage.removeItem('eld_ht'); } catch { /* no-op */ }
    try { localStorage.removeItem('eldiario_session'); } catch { /* no-op */ }
    try { localStorage.removeItem('eld_pending_plan_data'); } catch { /* no-op */ }
    // global: true revoca el refresh token en Cognito, impidiendo que se emitan
    // nuevos access tokens desde esta sesión incluso si el estado local fue comprometido.
    signOut({ global: true }).catch(() => {});
  }

  // ============ LOGOUT (explicit user action) ============
  async logout(syncServer = true): Promise<void> {
    // Capturar el token ANTES de limpiar la señal — lo necesitamos para
    // detectar si el usuario es federado (Google/Facebook) inspeccionando el scope del JWT.
    const currentAccessToken = this.accessToken();

    // Revocar el token en el backend ANTES de limpiar la señal —
    // el interceptor necesita el token en la cabecera Authorization.
    // Best-effort: si la llamada falla, igual se limpia la sesión local.
    if (syncServer) {
      try {
        await firstValueFrom(
          this.http.post(`${environment.apiUrl}/client/auth/logout`, {}, { withCredentials: true })
        );
      } catch { /* no-op — session cleared regardless */ }
    }

    this.accessToken.set(null);
    this.setCurrentUser(null);
    this.pendingRegistration = null;
    try { sessionStorage.removeItem('eld_ht'); } catch { /* no-op */ }
    // Limpiar la sesión en localStorage para que iframes del mismo dominio
    // (ej. /planes) no encuentren una sesión stale al volver a abrirse.
    try { localStorage.removeItem('eldiario_session'); } catch { /* no-op */ }
    try { localStorage.removeItem('eld_pending_plan_data'); } catch { /* no-op */ }

    // Detectar si el usuario inició sesión con Google/Facebook inspeccionando
    // el scope del JWT. Los usuarios federados no tienen el scope
    // 'aws.cognito.signin.user.admin', por lo que GlobalSignOut les devuelve 400.
    let isFederated = false;
    try {
      if (currentAccessToken) {
        const parts = currentAccessToken.split('.');
        const raw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padding = raw.length % 4 ? '='.repeat(4 - (raw.length % 4)) : '';
        const payload = JSON.parse(atob(raw + padding));
        const scopes: string = payload?.scope ?? '';
        isFederated = !scopes.includes('aws.cognito.signin.user.admin');
      }
    } catch { /* asumir usuario nativo */ }

    if (!isFederated) {
      // Usuario nativo: GlobalSignOut revoca el refresh token en Cognito.
      try {
        await signOut({ global: true });
      } catch {
        try { await signOut(); } catch { /* no-op */ }
      }
    } else {
      // Usuarios federados (Google/Facebook): NO se puede llamar signOut() porque
      // desencadena un redirect al hosted UI de Cognito (/logout?client_id=...) que
      // cancela nuestra navegación hacia WordPress con ?eld_logout=1, impidiendo que
      // events.js limpie la sesión del menú.
      // En su lugar, eliminamos manualmente todas las claves internas de Amplify/Cognito
      // del localStorage. Esto deja el estado de Amplify completamente limpio para que
      // el próximo login con Google funcione correctamente sin encontrar tokens stale.
      try {
        const amplifyKeys = Object.keys(localStorage).filter(k =>
          k.startsWith('CognitoIdentityServiceProvider.') || k.startsWith('amplify-')
        );
        amplifyKeys.forEach(k => { try { localStorage.removeItem(k); } catch { /* no-op */ } });
      } catch { /* no-op */ }
    }
  }

  // ============ PRIVATE HELPERS ============
  private async resolveSessionAfterSignUp(
    step: string,
    email: string,
    password: string,
  ): Promise<string | undefined> {
    if (step === 'COMPLETE_AUTO_SIGN_IN') {
      await autoSignIn();
    } else if (step === 'DONE') {
      await signIn({ username: email, password });
    }
    const { tokens } = await fetchAuthSession();
    return tokens?.accessToken?.toString();
  }

  private async saveProfileInBackend(data: PendingRegistration): Promise<UserProfile | null> {
    const anonymousId = this.getAnonymousId();
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; data: UserProfile }>(
        `${environment.apiUrl}/client/auth/register`,
        {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          phone_number: data.phone,
          marketing_consent: data.marketingConsent ? 1 : 0,
        },
        anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
      ),
    );
    return res.data ?? null;
  }

  // Public endpoint called after Cognito signUp and before confirmSignUp.
  // Deliberately non-blocking: registration can still recover through /register
  // after confirmation if a transient backend error occurs here.
  private preRegisterInBackend(data: PendingRegistration): void {
    firstValueFrom(
      this.http.post<{ success: boolean }>(
        `${environment.apiUrl}/client/auth/pre-register`,
        {
          cognito_sub: data.cognitoSub,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          phone_number: data.phone,
          marketing_consent: data.marketingConsent ? 1 : 0,
        },
      ),
    ).catch(error => console.warn('[preRegisterInBackend] Non-blocking error:', error));
  }

  // Called during auto-restore (embed ngOnInit) to sync with the backend and
  // trigger the anonymous → authenticated-user event migration.
  async ensureBackendLogin(): Promise<void> {
    const anonymousId = this.getAnonymousId();
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: UserProfile }>(
          `${environment.apiUrl}/client/auth/login`,
          {},
          anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
        )
      );
      this.setCurrentUser(res.data ?? null);
    } catch { /* best-effort — profile will be loaded by events flow */ }
  }

  /**
   * Espera a que Amplify complete el intercambio del código OAuth en la URL.
   * Usado en oauth-popup tras el redirect de Cognito.
   */
  async waitForOAuthSession(maxMs = 15000): Promise<boolean> {
    const deadline = Date.now() + maxMs;
    while (Date.now() < deadline) {
      if (await this.loadSessionToken()) return true;
      await new Promise(resolve => setTimeout(resolve, 250));
    }
    return false;
  }

  /**
   * Sincroniza el perfil OAuth con el backend y publica la sesión al padre WordPress
   * (popup o iframe embebido). Centraliza el flujo para evitar carreras Hub vs popup.
   */
  async finalizeOAuthPopupSession(): Promise<boolean> {
    const result = await this.syncOAuthProfileToBackend();
    if (!result.ok) return false;
    this.justLoggedIn.set(true);
    await this.postEmbeddedSessionToParent(result.isNewUser ? 'register' : 'login');
    return true;
  }

  // Syncs an OAuth (Google/Facebook) user with the backend.
  // Returning user → POST /login. New user (404) → auto-create profile via POST /register.
  private async syncOAuthProfileToBackend(): Promise<{ ok: boolean; isNewUser: boolean }> {
    try {
      const anonymousId = this.getAnonymousId();
      const oauthLoginRes = await firstValueFrom(
        this.http.post<{ success: boolean; data: UserProfile }>(
          `${environment.apiUrl}/client/auth/login`,
          {},
          anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
        )
      );
      this.setCurrentUser(oauthLoginRes.data ?? null);
      return { ok: true, isNewUser: false };
    } catch (loginError: any) {
      if (this.isProviderMismatchError(loginError)) {
        this.rejectOAuthProviderMismatch(loginError);
        return { ok: false, isNewUser: false };
      }
      if (loginError?.status === 404) {
        try {
          const { tokens } = await fetchAuthSession();
          const claims = tokens?.idToken?.payload ?? {};
          const profile = this.extractOAuthProfileFromClaims(claims);

          const cognitoUsername = String(claims['cognito:username'] ?? '').toLowerCase();
          const loginMethod = cognitoUsername.startsWith('facebook') ? 'facebook'
                            : cognitoUsername.startsWith('google')   ? 'google'
                            : 'email';

          const anonymousId = this.getAnonymousId();
          const oauthRegRes = await firstValueFrom(
            this.http.post<{ success: boolean; data: UserProfile }>(
              `${environment.apiUrl}/client/auth/register`,
              {
                email:             profile.email,
                first_name:        profile.firstName,
                last_name:         profile.lastName,
                phone_number:      (claims['phone_number'] as string) ?? null,
                marketing_consent: 0,
                login_method:      loginMethod,
              },
              anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
            ),
          );
          this.setCurrentUser(oauthRegRes.data ?? null);
          return { ok: true, isNewUser: true };
        } catch (registerError: any) {
          if (this.isProviderMismatchError(registerError)) {
            this.rejectOAuthProviderMismatch(registerError);
            return { ok: false, isNewUser: false };
          }
          // Carrera Hub + oauth-popup: el otro ya creó el perfil
          if (registerError?.status === 409) {
            try {
              const anonymousId = this.getAnonymousId();
              const retryRes = await firstValueFrom(
                this.http.post<{ success: boolean; data: UserProfile }>(
                  `${environment.apiUrl}/client/auth/login`,
                  {},
                  anonymousId ? { headers: { 'X-Anonymous-Id': anonymousId } } : undefined,
                )
              );
              this.setCurrentUser(retryRes.data ?? null);
              return { ok: true, isNewUser: false };
            } catch { /* fall through */ }
          }
          console.error('Error creating OAuth profile:', registerError);
          return { ok: false, isNewUser: false };
        }
      }
      console.error('Error syncing OAuth profile:', loginError);
      return { ok: false, isNewUser: false };
    }
  }

  /** Preflight for flows where the browser already knows the email. */
  private async checkProviderAllowed(
    email: string,
    requestedLoginMethod: 'email' | 'google' | 'facebook',
  ): Promise<ProviderCheckResult> {
    const normalizedEmail = String(email ?? '').trim();
    if (!normalizedEmail) return { success: true };

    try {
      await firstValueFrom(
        this.http.post<{ success: boolean; allowed: boolean }>(
          `${environment.apiUrl}/client/auth/provider-check`,
          { email: normalizedEmail, requested_login_method: requestedLoginMethod },
        ),
      );
      return { success: true };
    } catch (error: any) {
      const mismatch = this.providerMismatchResult(error);
      if (mismatch) return mismatch;
      // The authenticated backend guard remains authoritative after sign-in.
      // Do not make Cognito unavailable because this preflight is temporarily down.
      return { success: true };
    }
  }

  private isProviderMismatchError(error: any): boolean {
    return error?.status === 409 && error?.error?.code === 'AUTH_PROVIDER_MISMATCH';
  }

  private providerMismatchResult(error: any): ProviderCheckResult | null {
    if (!this.isProviderMismatchError(error)) return null;
    return {
      success: false,
      errorName: 'AUTH_PROVIDER_MISMATCH',
      error: String(error.error?.message ?? 'Usa el método con el que creaste tu cuenta.'),
    };
  }

  private rejectOAuthProviderMismatch(error: any): void {
    const message = String(error?.error?.message ?? 'Usa el método con el que creaste tu cuenta.');
    this.oauthProviderError.set(message);
    try { sessionStorage.setItem(this.PROVIDER_MISMATCH_KEY, message); } catch { /* no-op */ }

    // Avoid Hosted UI sign-out here: it would hide the error after an OAuth redirect.
    this.clearAmplifySession().catch(() => {});
  }

  consumeProviderMismatchMessage(): string | null {
    const inMemory = this.oauthProviderError();
    this.oauthProviderError.set(null);
    try {
      const persisted = sessionStorage.getItem(this.PROVIDER_MISMATCH_KEY);
      sessionStorage.removeItem(this.PROVIDER_MISMATCH_KEY);
      return inMemory ?? persisted;
    } catch {
      return inMemory;
    }
  }

  /** Clears local OAuth state without redirecting to Cognito Hosted UI. */
  async clearAmplifySession(): Promise<void> {
    this.accessToken.set(null);
    this.setCurrentUser(null);
    this.pendingRegistration = null;
    try { sessionStorage.removeItem('eld_ht'); } catch { /* no-op */ }
    try { localStorage.removeItem('eldiario_session'); } catch { /* no-op */ }
    try {
      const amplifyKeys = Object.keys(localStorage).filter(k =>
        k.startsWith('CognitoIdentityServiceProvider.') || k.startsWith('amplify-')
      );
      amplifyKeys.forEach(k => { try { localStorage.removeItem(k); } catch { /* no-op */ } });
    } catch { /* no-op */ }
  }

  /** Normaliza nombre/apellido desde claims OAuth (Google/Facebook varían). */
  private extractOAuthProfileFromClaims(claims: Record<string, unknown>): {
    email: string;
    firstName: string;
    lastName: string;
  } {
    const email = String(claims['email'] ?? '').trim();
    let firstName = String(claims['given_name'] ?? '').trim();
    let lastName = String(claims['family_name'] ?? '').trim();

    if (!firstName && !lastName) {
      const full = String(claims['name'] ?? '').trim();
      if (full) {
        const parts = full.split(/\s+/).filter(Boolean);
        firstName = parts[0] ?? '';
        lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
      }
    }
    if (!firstName && email.includes('@')) {
      firstName = email.split('@')[0];
    }
    if (!firstName) firstName = 'Usuario';
    if (!lastName) lastName = '—';

    return { email, firstName, lastName };
  }

  private getAnonymousId(): string {
    const key = 'px_anon_id';
    try {
      try {
        const params = new URLSearchParams(window.location.search);
        const fromQuery =
          (params.get('anon_id') || params.get('anonymous_id') || params.get('px_anon_id') || '').trim();
        if (fromQuery.length === 36) {
          localStorage.setItem(key, fromQuery);
          return fromQuery;
        }
      } catch {}

      const existing = localStorage.getItem(key);
      if (existing && existing.trim().length === 36) return existing.trim();

      const created = this.createUuid();
      localStorage.setItem(key, created);
      return created;
    } catch {
      return '';
    }
  }

  private createUuid(): string {
    try {
      const c = (globalThis as any).crypto;
      if (c && typeof c.randomUUID === 'function') return String(c.randomUUID());
    } catch {}

    const buf = new Uint8Array(16);
    try {
      const c = (globalThis as any).crypto;
      if (c && typeof c.getRandomValues === 'function') c.getRandomValues(buf);
      else for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    } catch {
      for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    }

    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;

    const hex = Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // ============ GET PROFILE ============
  async getProfile(): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; data: UserProfile }>(`${environment.apiUrl}/usuarios/perfil`)
      );
      this.setCurrentUser(res.data ?? null);
      return true;
    } catch {
      return false;
    }
  }

  // ============ UPDATE PROFILE ============
  async updateProfile(data: UpdateProfilePayload): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await firstValueFrom(
        this.http.put<{ success: boolean; data: UserProfile }>(`${environment.apiUrl}/usuarios/perfil`, data)
      );
      this.setCurrentUser(res.data ?? null);
      return { success: true };
    } catch {
      return { success: false, error: 'No se pudo actualizar el perfil. Intenta de nuevo.' };
    }
  }

  async deleteAccount(): Promise<{ success: boolean; error?: string }> {
    try {
      await firstValueFrom(
        this.http.delete<{ success: boolean }>(`${environment.apiUrl}/usuarios/perfil`, { withCredentials: true }),
      );
      // The backend revoked the token as part of the deletion. Clear only the
      // local session; a protected logout request would correctly be rejected.
      await this.logout(false);
      return { success: true };
    } catch (err: any) {
      return {
        success: false,
        error: err?.error?.message ?? 'No se pudo eliminar la cuenta. Intenta nuevamente o contacta soporte.',
      };
    }
  }

  async updateNewsletterConsent(value: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      await firstValueFrom(
        this.http.put<{ success: boolean; data: { marketing_consent: boolean } }>(
          `${environment.apiUrl}/usuarios/newsletter-consent`,
          { newsletter_consent: value },
        )
      );

      const user = this.currentUser();
      if (user) {
        this.setCurrentUser({ ...user, marketing_consent: value });
      }

      return { success: true };
    } catch {
      return { success: false, error: 'No se pudo actualizar la preferencia de boletines.' };
    }
  }

  async updateDocumentInfo(
    docType: 'CC' | 'TI' | 'CE' | 'PA' | 'NIT' | null,
    docNumber: string | null,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await firstValueFrom(
        this.http.put<{ success: boolean }>(`${environment.apiUrl}/usuarios/documento`, {
          doc_type: docType,
          doc_number: docNumber,
        })
      );
      const user = this.currentUser();
      if (user) {
        this.setCurrentUser({ ...user, doc_type: docType, doc_number: docNumber });
      }
      return { success: true };
    } catch {
      return { success: false, error: 'No se pudo guardar el documento. Intenta de nuevo.' };
    }
  }

  // ============ CHANGE PASSWORD (Cognito) ============
  async changePassword(oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await updatePassword({ oldPassword, newPassword });
      return { success: true };
    } catch (error: any) {
      const message = error?.name === 'NotAuthorizedException'
        ? 'La contraseña actual es incorrecta'
        : this.translateError(error.name);
      return { success: false, error: message, errorName: error.name };
    }
  }

  // ============ FORGOT PASSWORD — STEP 1 (send code) ============
  async forgotPassword(email: string): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await resetPassword({ username: email });
      return { success: true };
    } catch (error: any) {
      // Return success even for UserNotFoundException to prevent email enumeration (OWASP A07)
      if (error?.name === 'UserNotFoundException') return { success: true };
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    }
  }

  // ============ FORGOT PASSWORD — STEP 2 (confirm code + new password) ============
  async confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<{ success: boolean; error?: string; errorName?: string }> {
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: this.translateError(error.name), errorName: error.name };
    }
  }

  // ============ TRANSLATE ERRORS ============
  private translateError(errorName: string): string {
    const errors: Record<string, string> = {
      // Register
      UsernameExistsException: 'Este correo ya está registrado',
      InvalidPasswordException: 'La contraseña no cumple los requisitos mínimos',
      InvalidParameterException: 'Datos inválidos, revisa los campos',
      CodeMismatchException: 'Código incorrecto, inténtalo de nuevo',
      ExpiredCodeException: 'El código ha expirado, solicita uno nuevo',
      // Login — NotAuthorizedException y UserNotFoundException devuelven el mismo mensaje
      // para evitar enumeración de usuarios (OWASP A07)
      NotAuthorizedException: 'Correo o contraseña incorrectos',
      UserNotFoundException: 'Correo o contraseña incorrectos',
      UserNotConfirmedException: 'Debes verificar tu correo antes de continuar',
      PasswordResetRequiredException: 'Debes restablecer tu contraseña',
      // Shared
      LimitExceededException: 'Demasiados intentos, espera un momento',
      TooManyRequestsException: 'Demasiados intentos, espera un momento',
      NetworkError: 'Error de conexión, verifica tu internet',
    };
    return errors[errorName] ?? 'Ocurrió un error inesperado';
  }

  private isEmbeddedContext(): boolean {
    try {
      const embed = new URLSearchParams(window.location.search).get('embed');
      if (embed === '1' || embed === 'true') return true;
    } catch {}
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }
}
